// =============================================================================
// Origin UI — src/pages/assets/AssetReportsPage.tsx
// IT Asset Reports — 3 tabs: By Office, By Type, Warranty Expiring
// With CSV export on each tab.
// =============================================================================

import { useState, useEffect } from 'react';
import { assetApi, AssetsByOffice, AssetsByType, WarrantyExpiring } from '@/services/assetApi';
import styles from './assets.module.css';

type ReportTab = 'by-office' | 'by-type' | 'warranty';

export function AssetReportsPage() {
  const [tab, setTab] = useState<ReportTab>('by-office');
  const [officeData, setOfficeData] = useState<AssetsByOffice[]>([]);
  const [typeData, setTypeData] = useState<AssetsByType[]>([]);
  const [warrantyData, setWarrantyData] = useState<WarrantyExpiring[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      setLoading(true);
      setError('');
      const [offices, types, warranty] = await Promise.all([
        assetApi.reportByOffice(),
        assetApi.reportByType(),
        assetApi.reportWarrantyExpiring(90),
      ]);
      setOfficeData(offices);
      setTypeData(types);
      setWarrantyData(warranty);
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  }

  async function handleCsvExport() {
    try { await assetApi.exportCsv({}); } catch (err: any) { setError(err.message); }
  }

  function formatDate(d: string): string {
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  const maxOfficeTotal = Math.max(...officeData.map(o => o.total), 1);
  const maxTypeTotal = Math.max(...typeData.map(t => t.total), 1);

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>IT Asset Reports</h1>
          <div className={styles.pageSubtitle}>Asset distribution, fleet overview, and warranty tracking</div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryBtn} onClick={handleCsvExport}>📥 Export All CSV</button>
        </div>
      </div>

      {error && (
        <div className={styles.errorBanner}>
          {error}
          <button className={styles.errorDismiss} onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className={styles.reportTabs}>
        {([
          ['by-office', 'Assets by Office'],
          ['by-type', 'Assets by Type'],
          ['warranty', 'Warranty Expiring'],
        ] as [ReportTab, string][]).map(([key, label]) => (
          <button
            key={key}
            className={`${styles.reportTab} ${tab === key ? styles.reportTabActive : ''}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.emptyText}>Loading reports...</div>
      ) : (
        <>
          {/* By Office */}
          {tab === 'by-office' && (
            <div className={styles.card}>
              {/* Chart */}
              <div className={styles.chartContainer}>
                {officeData.map(o => (
                  <div key={o.officeName} className={styles.chartBar}>
                    <div className={styles.chartLabel}>{o.officeName}</div>
                    <div className={styles.chartBarTrack}>
                      {o.assigned > 0 && <div className={styles.chartBarSegment} style={{ width: `${(o.assigned / maxOfficeTotal) * 100}%`, background: '#00AA6E' }} />}
                      {o.pendingReturn > 0 && <div className={styles.chartBarSegment} style={{ width: `${(o.pendingReturn / maxOfficeTotal) * 100}%`, background: '#FF8300' }} />}
                      {(o.returned + o.lost + o.disposed) > 0 && <div className={styles.chartBarSegment} style={{ width: `${((o.returned + o.lost + o.disposed) / maxOfficeTotal) * 100}%`, background: '#CDD4DB' }} />}
                    </div>
                    <div className={styles.chartBarCount}>{o.total}</div>
                  </div>
                ))}
                <div className={styles.chartLegend}>
                  <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#00AA6E' }} />Assigned</span>
                  <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#FF8300' }} />Pending Return</span>
                  <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#CDD4DB' }} />Returned/Other</span>
                </div>
              </div>

              {/* Table */}
              <table className={styles.reportTable}>
                <thead>
                  <tr>
                    <th>Office</th>
                    <th>Total</th>
                    <th>Assigned</th>
                    <th>Pending Return</th>
                    <th>Returned</th>
                    <th>Lost</th>
                    <th>Disposed</th>
                  </tr>
                </thead>
                <tbody>
                  {officeData.map(o => (
                    <tr key={o.officeName}>
                      <td style={{ fontWeight: 600, color: '#0A2240' }}>{o.officeName}</td>
                      <td className={styles.reportNumber}>{o.total}</td>
                      <td>{o.assigned}</td>
                      <td style={{ color: o.pendingReturn > 0 ? '#FF8300' : undefined, fontWeight: o.pendingReturn > 0 ? 600 : undefined }}>{o.pendingReturn}</td>
                      <td>{o.returned}</td>
                      <td>{o.lost}</td>
                      <td>{o.disposed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* By Type */}
          {tab === 'by-type' && (
            <div className={styles.card}>
              {/* Chart */}
              <div className={styles.chartContainer}>
                {typeData.map(t => (
                  <div key={t.assetType} className={styles.chartBar}>
                    <div className={styles.chartLabel}>{t.assetType}</div>
                    <div className={styles.chartBarTrack}>
                      {t.assigned > 0 && <div className={styles.chartBarSegment} style={{ width: `${(t.assigned / maxTypeTotal) * 100}%`, background: '#00B0B9' }} />}
                      {t.pendingReturn > 0 && <div className={styles.chartBarSegment} style={{ width: `${(t.pendingReturn / maxTypeTotal) * 100}%`, background: '#FF8300' }} />}
                      {(t.returned + t.lost + t.disposed) > 0 && <div className={styles.chartBarSegment} style={{ width: `${((t.returned + t.lost + t.disposed) / maxTypeTotal) * 100}%`, background: '#CDD4DB' }} />}
                    </div>
                    <div className={styles.chartBarCount}>{t.total}</div>
                  </div>
                ))}
                <div className={styles.chartLegend}>
                  <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#00B0B9' }} />Assigned</span>
                  <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#FF8300' }} />Pending Return</span>
                  <span className={styles.legendItem}><span className={styles.legendDot} style={{ background: '#CDD4DB' }} />Returned/Other</span>
                </div>
              </div>

              {/* Table */}
              <table className={styles.reportTable}>
                <thead>
                  <tr>
                    <th>Asset Type</th>
                    <th>Total</th>
                    <th>Assigned</th>
                    <th>Pending Return</th>
                    <th>Returned</th>
                    <th>Lost</th>
                    <th>Disposed</th>
                    <th>Avg Age (days)</th>
                  </tr>
                </thead>
                <tbody>
                  {typeData.map(t => (
                    <tr key={t.assetType}>
                      <td style={{ fontWeight: 600, color: '#0A2240' }}>{t.assetType}</td>
                      <td className={styles.reportNumber}>{t.total}</td>
                      <td>{t.assigned}</td>
                      <td style={{ color: t.pendingReturn > 0 ? '#FF8300' : undefined, fontWeight: t.pendingReturn > 0 ? 600 : undefined }}>{t.pendingReturn}</td>
                      <td>{t.returned}</td>
                      <td>{t.lost}</td>
                      <td>{t.disposed}</td>
                      <td>{Math.round(t.averageAgeDays)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Warranty Expiring */}
          {tab === 'warranty' && (
            <div className={styles.card}>
              {warrantyData.length === 0 ? (
                <div className={styles.emptyText}>No assets with warranty expiring in the next 90 days.</div>
              ) : (
                <table className={styles.reportTable}>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Office</th>
                      <th>Asset Type</th>
                      <th>Make / Model</th>
                      <th>Asset Tag</th>
                      <th>Warranty Expiry</th>
                      <th>Days Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {warrantyData.map(w => (
                      <tr key={w.assetId}>
                        <td>
                          <div style={{ fontWeight: 600, color: '#0A2240' }}>{w.employeeName}</div>
                          <div style={{ fontSize: '11px', color: '#9BA5B0', fontFamily: 'monospace' }}>{w.employeeNumber}</div>
                        </td>
                        <td style={{ fontSize: '12px', color: '#6B7885' }}>{w.officeName}</td>
                        <td>{w.assetType}</td>
                        <td style={{ fontSize: '12px', color: '#4A5568' }}>{w.makeModel || '—'}</td>
                        <td>{w.assetTag ? <span className={styles.assetTagCode}>{w.assetTag}</span> : '—'}</td>
                        <td>
                          <span className={w.daysRemaining < 0 ? styles.warrantyExpired : styles.warrantyExpiring}>
                            {formatDate(w.warrantyExpiryDate)}
                          </span>
                        </td>
                        <td style={{
                          fontWeight: 700,
                          color: w.daysRemaining < 0 ? '#F04B41' : w.daysRemaining <= 30 ? '#FF8300' : '#0A2240',
                        }}>
                          {w.daysRemaining < 0 ? `${Math.abs(w.daysRemaining)}d overdue` : `${w.daysRemaining}d`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
